import {
  aboutBio,
  aboutPortrait,
  collagePages,
  featuredCover,
  focusAreas,
  heroPortrait,
  profileAuthorityLinks,
  publications,
  writer,
} from "../mock";
import { DEFAULT_NAV_ITEMS } from "./navigation";

const section = ({
  page,
  sectionKey,
  sectionName,
  title = "",
  subtitle = "",
  label = "",
  eyebrow = "",
  body = "",
  caption = "",
  imageUrl = "",
  imageAlt = "",
  imagePosition = "center",
  imageFit = "cover",
  ctaLabel = "",
  ctaUrl = "",
  isVisible = true,
  sortOrder = 0,
  style = {},
  content = {},
}) => ({
  id: sectionKey,
  page,
  sectionKey,
  sectionName,
  title,
  subtitle,
  label,
  eyebrow,
  body,
  caption,
  imageUrl,
  imageAlt,
  imagePosition,
  imageFit,
  ctaLabel,
  ctaUrl,
  isVisible,
  sortOrder,
  style,
  content,
});

const seoPageSections = [
  section({
    page: "portfolio",
    sectionKey: "portfolio.hero",
    sectionName: "Portfolio Hero",
    eyebrow: "Freelance Journalist - Cairo, Egypt - 2023-Present",
    title: "JOURNALIST\nPORTFOLIO",
    subtitle:
      "Mai El Mokadem is a freelance journalist and editorial writer based in Egypt, covering fashion, music, and culture across the Middle East and beyond.",
    body:
      "Mai El Mokadem is an Egyptian freelance journalist covering the cultural and creative scene in Egypt and the Middle East and North Africa.",
    sortOrder: 10,
    content: {
      links: [
        { label: "Fashion Journalism", url: "/fashion-journalist" },
        { label: "Music Journalism", url: "/music-journalist" },
        { label: "Cultural Commentary", url: "/cultural-commentary" },
        { label: "Interviews", url: "/interviews" },
      ],
    },
  }),
  section({
    page: "portfolio",
    sectionKey: "portfolio.intro",
    sectionName: "Portfolio Intro",
    title: "FIVE YEARS OF BYLINES ACROSS THE ARAB WORLD",
    body:
      "There are journalists who cover a scene, and journalists who are embedded in it. Mai El Mokadem belongs to the second kind.\n\nHer portfolio spans some of the most respected editorial platforms in the region: Dazed MENA, Rolling Stone MENA, This Is YUNG, A Shot Magazine, Scene Styled, Cairo Scene, and Scene Noise.",
    sortOrder: 20,
    content: {
      badges: [
        "Fashion & Designers",
        "Music & Sonic Identity",
        "Cultural Long-Reads",
        "Celebrity Cover Interviews",
        "Egypt & Cairo Scene",
      ],
    },
  }),
  section({
    page: "portfolio",
    sectionKey: "portfolio.featured-work",
    sectionName: "Portfolio Featured Work",
    title: "FASHION. MUSIC. CULTURE.",
    body: "Three entry points into the archive: fashion journalism, music criticism, and cultural commentary.",
    sortOrder: 30,
  }),
  section({
    page: "portfolio",
    sectionKey: "portfolio.external-work",
    sectionName: "Portfolio External Work",
    label: "Where the bylines live",
    title: "PUBLICATIONS",
    body:
      "Public references include external work for Scene Eats and YUNG MEA, with profile authority from SceneNow and International Music Summit.",
    sortOrder: 40,
    content: { items: publications },
  }),
  section({
    page: "fashion",
    sectionKey: "fashion.hero",
    sectionName: "Fashion Hero",
    eyebrow: "Fashion Journalism - Egypt & Middle East",
    title: "FASHION\nJOURNALIST",
    subtitle:
      "Documenting Egyptian and Middle Eastern fashion from the inside - cover stories, designer profiles, and style commentary that treats clothing as cultural evidence.",
    body:
      "In fashion journalism, it is not enough to describe clothes - the work is to understand what they say about identity.",
    sortOrder: 10,
  }),
  section({
    page: "fashion",
    sectionKey: "fashion.intro",
    sectionName: "Fashion Intro",
    title: "THE FASHION BEAT: FROM CAIRO TO THE FRONT ROW",
    body:
      "Fashion journalism in Egypt is not about trend reports. It is about documentation.\n\nMai El Mokadem's fashion coverage is anchored at Scene Styled, where she has produced cover stories on Egyptian and regional designers, photographers, and creative figures shaping the new front row.",
    sortOrder: 20,
    content: {
      badges: [
        "Egyptian Designer Profiles",
        "Cairo Streetwear & Style",
        "Paris Fashion Week",
        "Cover Story Interviews",
      ],
    },
  }),
  section({
    page: "fashion",
    sectionKey: "fashion.featured-work",
    sectionName: "Fashion Featured Work",
    title: "FASHION STORIES",
    body: "Designer profiles, cover stories, and style features from the archive.",
    ctaLabel: "Get in Touch",
    ctaUrl: "mailto:maielmokadem@outlook.com",
    sortOrder: 30,
  }),
  section({
    page: "music",
    sectionKey: "music.hero",
    sectionName: "Music Hero",
    eyebrow: "Music Journalism - Arab Sound - Cultural Commentary",
    title: "MUSIC\nJOURNALIST",
    subtitle:
      "Writing Arab sonic identity for global audiences - music journalism that understands what's at stake when a culture's sound becomes the world's aesthetic.",
    body:
      "Arab music is living through a real transformation between originality, globalization, identity, and experimentation.",
    sortOrder: 10,
    content: {
      stats: [
        { value: "19K+", label: "TikTok likes" },
        { value: "7.8K", label: "Instagram reach" },
        { value: "Dubai", label: "Panel speaker" },
      ],
    },
  }),
  section({
    page: "music",
    sectionKey: "music.intro",
    sectionName: "Music Intro",
    title: "THE PIECE THAT CHANGED THE CONVERSATION",
    body:
      "In 2025, a feature article in Dazed MENA asked what happens when the Arab world's sonic heritage becomes a global aesthetic trend, divorced from the culture that produced it.\n\nThe piece generated 19,000 likes on TikTok, 7,800 on Instagram, a Dubai panel invitation, and a citation inside a Rolling Stone MENA feature on DJ Snake.",
    sortOrder: 20,
    content: {
      badges: [
        "Arab Sonic Identity",
        "Egyptian Music Scene",
        "Regional Club Culture",
        "Music x Cultural Politics",
      ],
    },
  }),
  section({
    page: "music",
    sectionKey: "music.featured-work",
    sectionName: "Music Featured Work",
    title: "CULTURE & SOUND",
    body: "Music journalism, cultural criticism, and sound identity from the archive.",
    ctaLabel: "Get in Touch",
    ctaUrl: "mailto:maielmokadem@outlook.com",
    sortOrder: 30,
  }),
  section({
    page: "culture",
    sectionKey: "culture.hero",
    sectionName: "Culture Hero",
    eyebrow: "Cultural Commentary - Long-Form Writing - Middle East",
    title: "CULTURAL\nCOMMENTARY",
    subtitle:
      "Long-form essays at the intersection of identity, image, and the Arab world - journalism that names what's happening in the culture before the culture has found the words for it.",
    body:
      "Arab culture is a living space that is remade every day through image, intimacy, identity, internet culture, and memory.",
    sortOrder: 10,
  }),
  section({
    page: "culture",
    sectionKey: "culture.intro",
    sectionName: "Culture Intro",
    title: "THE LONG-FORM ESSAY PRACTICE",
    body:
      "The most useful journalism is not the journalism that tells you what happened. It is the journalism that tells you what it means.\n\nCultural commentary allows a journalist to slow down, accumulate evidence, and make an argument that does not fit inside a caption or news brief.",
    sortOrder: 20,
    content: {
      badges: ["Love & Intimacy", "Arab Horror", "Internet Culture", "Gender & Public Space"],
    },
  }),
  section({
    page: "culture",
    sectionKey: "culture.featured-work",
    sectionName: "Culture Featured Work",
    title: "CULTURAL LONG-READS",
    body: "Essays and long-form cultural commentary from the archive.",
    ctaLabel: "Get in Touch",
    ctaUrl: "mailto:maielmokadem@outlook.com",
    sortOrder: 30,
  }),
  section({
    page: "interviews",
    sectionKey: "interviews.hero",
    sectionName: "Interviews Hero",
    eyebrow: "Cover Interviews - Artist Profiles - A Shot Magazine",
    title: "INTER\nVIEWS",
    subtitle:
      "In-depth conversations with the regional and global creative figures shaping art, fashion, music, and culture - profiles that go beyond the press release to find the person inside the story.",
    body:
      "A good interview is not just questions and answers; it is a conversation that reveals what sits behind the public image.",
    sortOrder: 10,
  }),
  section({
    page: "interviews",
    sectionKey: "interviews.intro",
    sectionName: "Interviews Intro",
    title: "THE CONVERSATION BEHIND THE COVER",
    body:
      "A cover interview is one of the most demanding forms in journalism. It requires months of preparation, hours of conversation, and editorial discipline.\n\nAs a lead writer at A Shot Magazine, Mai El Mokadem produces cover interviews that regional and international creatives reserve for publications they trust.",
    sortOrder: 20,
    content: {
      badges: [
        "Cover Profile Interviews",
        "Artist & Photographer Profiles",
        "Designer Deep-Dives",
        "Music Artist Conversations",
      ],
    },
  }),
  section({
    page: "interviews",
    sectionKey: "interviews.featured-work",
    sectionName: "Interviews Featured Work",
    title: "COVER CONVERSATIONS",
    body: "Cover profiles and in-depth conversations from the archive.",
    ctaLabel: "Get in Touch",
    ctaUrl: "mailto:maielmokadem@outlook.com",
    sortOrder: 30,
  }),
];

export const defaultSiteSections = [
  section({
    page: "home",
    sectionKey: "home.hero",
    sectionName: "Homepage Hero",
    eyebrow: "The Writing Portfolio - Front Cover",
    label: "The Byline - Issue 26",
    title: "MAI EL\nMOKADEM.",
    subtitle: writer.bio,
    caption: "written, not styled.",
    imageUrl: heroPortrait,
    imageAlt: "Editorial portrait",
    imagePosition: "center",
    ctaLabel: "CONTACT ME FOR COMMISSIONS, PITCHES, HIRE ME",
    ctaUrl: "/journalist-portfolio",
    sortOrder: 10,
    content: {
      metaLeft: `VOL. 01 - ${writer.issue} - ${writer.year}`,
      metaCenter: "For the byline. For the beat.",
      metaRight: "CAIRO -> WORLDWIDE",
      issueStamp: "EDITORIAL ISSUE",
      tags: writer.taglineLines,
      handwrittenCta: "start here ->",
    },
  }),
  section({
    page: "home",
    sectionKey: "home.hero-side-panel",
    sectionName: "Homepage Hero Side Panel",
    label: "IN THIS ISSUE",
    body: '"the region is the room."',
    sortOrder: 20,
    content: {
      items: [
        "P.04 - The Habibification of House Music",
        "P.12 - Sarah Sahabati, in her own words",
        "P.18 - In the Arab World, Love Walks on Tiptoe",
        "P.24 - Tara Babylon, born of an imaginary aura",
      ],
    },
  }),
  section({
    page: "home",
    sectionKey: "home.featured-cover",
    sectionName: "Homepage Featured Cover",
    eyebrow: "Featured",
    label: featuredCover.kicker,
    title: featuredCover.title,
    subtitle: featuredCover.category,
    body: featuredCover.dek,
    caption: featuredCover.pullQuote,
    imageUrl: featuredCover.image,
    imageAlt: featuredCover.title,
    imagePosition: "top",
    ctaLabel: "BROWSE THE PORTFOLIO",
    ctaUrl: "/journalist-portfolio",
    sortOrder: 30,
    content: {
      publication: featuredCover.publication,
      category: featuredCover.category,
      date: featuredCover.date,
      stats: featuredCover.stats,
    },
  }),
  section({
    page: "home",
    sectionKey: "home.article-grid-intro",
    sectionName: "Homepage Article Grid Intro",
    eyebrow: "Section 02 - The Clippings",
    title: "STORIES,\nFILED.",
    body:
      "A working archive of cover features, interviews and culture pieces - cut from the page, pinned to the wall.",
    caption: "more in the archive",
    sortOrder: 40,
  }),
  section({
    page: "home",
    sectionKey: "home.writing-beats",
    sectionName: "Homepage Brief Image",
    eyebrow: "Section 06 - The Brief",
    title: "I write the region into the room - before someone else writes it out.",
    body:
      "High-stakes, real-time storytelling on the cultural forces shaping music, identity, and style across the Middle East. Cover features. Cover stories. The byline that knows the room.",
    caption: "This is YUNG // Pitches & Ideas",
    imageUrl: collagePages.thisisyung,
    imageAlt: "This Is YUNG editorial spreads",
    sortOrder: 50,
    content: { badges: ["Cover Stories", "Long-reads", "Profiles", "Edits", "Cultural Commentary"] },
  }),
  section({
    page: "home",
    sectionKey: "home.public-proof",
    sectionName: "Homepage Public Proof",
    eyebrow: "Section 04 - Highlights",
    title: "THEN\nTHE PIECE\nBLEW UP.",
    body: "Receipts from the byline - viral pieces, panels, and citations.",
    caption: "dubai panel + dazed mena spread",
    imageUrl: collagePages.dazed,
    imageAlt: "Dazed coverage and Dubai panel",
    sortOrder: 60,
  }),
  section({
    page: "home",
    sectionKey: "home.pull-quotes",
    sectionName: "Homepage Pull Quotes",
    eyebrow: "Section 05 - Pull Quotes",
    imageUrl: collagePages.yung,
    imageAlt: "YUNG editorial spread",
    sortOrder: 70,
    content: { limit: "" },
  }),
  section({
    page: "home",
    sectionKey: "home.contact",
    sectionName: "Homepage Contact",
    eyebrow: "Section 07 - Letters to the Editor",
    title: "PITCH\nME.",
    body: "Cover features, profiles, fashion, music, culture, panels. Briefs welcome.",
    ctaLabel: "Request Inquiry",
    sortOrder: 80,
    content: {
      formStamp: "FOR COMMISSION",
      formHeading: "Inquiry form",
      nameLabel: "Name",
      namePlaceholder: "Your name",
      emailLabel: "Email",
      emailPlaceholder: "your@email.com",
      messageLabel: "The Brief",
      messagePlaceholder: "Tell me about the story, commission, interview, or collaboration.",
    },
  }),
  section({
    page: "about",
    sectionKey: "about.hero",
    sectionName: "About Hero",
    eyebrow: "The Writing Portfolio - Inside Cover",
    label: `COLOPHON - ABOUT - ${writer.year}`,
    title: "ABOUT THE\nwriter.",
    subtitle: "The writer behind the byline.",
    sortOrder: 10,
  }),
  section({
    page: "about",
    sectionKey: "about.bio",
    sectionName: "About Bio",
    eyebrow: "Section 01 - The Bio",
    body: aboutBio.join("\n\n"),
    caption: "Portrait - Editorial // Archival",
    imageUrl: aboutPortrait,
    imageAlt: "Mai El Mokadem",
    imagePosition: "center",
    sortOrder: 20,
  }),
  section({
    page: "about",
    sectionKey: "about.public-proof",
    sectionName: "About Public Proof",
    eyebrow: "Featured Profiles & Appearances",
    title: "PUBLIC\nPROOF.",
    body:
      "Mai's work and public profile have appeared across regional editorial, culture, fashion, music, and media platforms including Scene Eats, YUNG MEA, SceneNow, and International Music Summit.",
    sortOrder: 30,
  }),
  section({
    page: "about",
    sectionKey: "about.authority-links",
    sectionName: "About Authority Links",
    title: "Authority Links",
    sortOrder: 40,
    content: { links: profileAuthorityLinks },
  }),
  section({
    page: "about",
    sectionKey: "about.focus-areas",
    sectionName: "About Focus Areas",
    eyebrow: "Section 02 - The Beat",
    title: "WRITING\nFOCUS.",
    body: "Where the byline lives - the cultural forces shaping a region in constant evolution.",
    sortOrder: 50,
    content: { items: focusAreas },
  }),
    section({
    page: "about",
    sectionKey: "about.career-files",
    sectionName: "About Career Files Image",
    eyebrow: "Section 03 - The Spine",
    title: "CAREER\nFILES.",
    subtitle: "A working timeline of the bylines that built the beat.",
    imageUrl: collagePages.styled1,
    imageAlt: "Scene Styled covers",
    imageFit: "cover",
    imagePosition: "center",
    sortOrder: 55,
  }),
  section({
    page: "about",
    sectionKey: "about.image",
    sectionName: "About Image",
    caption: "the byline.",
    imageUrl: aboutPortrait,
    imageAlt: "Mai El Mokadem",
    imagePosition: "center",
    sortOrder: 60,
  }),
  ...seoPageSections,
  section({
    page: "global",
    sectionKey: "global.header",
    sectionName: "Global Header",
    title: "THE WRITING PORTFOLIO.",
    label: writer.shortName,
    ctaLabel: "Contact",
    ctaUrl: "/#contact",
    sortOrder: 10,
    content: { nav: DEFAULT_NAV_ITEMS, contactVisible: true },
  }),
  section({
    page: "global",
    sectionKey: "global.footer",
    sectionName: "Global Footer",
    title: "MAI EL\nMOKADEM.",
    body: "Writing the region into the room before someone else writes it out.",
    label: `The Writing Portfolio - ${writer.issue}`,
    caption: "Printed in Cairo - Distributed Worldwide",
    sortOrder: 20,
  }),
  section({
    page: "global",
    sectionKey: "global.social-links",
    sectionName: "Global Social Links",
    sortOrder: 30,
    content: {
      email: writer.email,
      instagram: writer.instagram,
      instagramUrl: writer.instagramUrl,
    },
  }),
  section({
    page: "global",
    sectionKey: "global.publications",
    sectionName: "Global Publications",
    title: "Publications",
    sortOrder: 40,
    content: { items: publications },
  }),
  section({
    page: "global",
    sectionKey: "global.seo-strip",
    sectionName: "Global SEO Strip",
    title: "Pitches welcome",
    sortOrder: 50,
    content: {
      items: ["Pitches welcome", "Cover stories", "Long-reads", "Profiles", "Edits", "Cultural commentary"],
    },
  }),
];

export default defaultSiteSections;
